// ═══════════════════════════════════════════════════════════════════════
// Croupier banded zone chip stack — pure helpers.
//
// v12.3 introduced the GROWING stack (depth = a monotone map of the staked
// amount); v12.4 raised the drawing to real poker chips (SVG face + striped
// rim at a shallow table angle). v13 makes the stack TRUTHFUL the way a
// croupier's is: the staked amount is greedily decomposed over the strip's
// denomination ladder and drawn as one banded pile — largest denomination
// at the BOTTOM, bands stacked upward in ladder order, each disc in its own
// family palette (€162 → green 100 at the base, orange 50, blue 10, two
// grey 1s on top). Everything here stays PURE and deterministic:
//
//   - composition: chipStackComposition — greedy largest-first over the
//     ladder, sub-minimum stakes round UP to one smallest chip, fractional
//     remainders fold into the smallest-chip count, and an explicit
//     deterministic compression keeps the pile at CHIP_STACK_MAX discs
//     (rules documented on the function).
//   - discs/keys: chipStackDiscs flattens the bands bottom→top with
//     band-relative keys (`denom:i`) so growing a band keeps every existing
//     disc's React node; chipStackSettleKey decides which disc (if any)
//     plays the settle animation — a restructure settles NOTHING.
//   - layout: per-index offsets/rotations derived FROM the index (no
//     Math.random at render — a remount draws the pixel-identical pile).
//     Real stacks are NEAT: offsets stay within ±3px, rotations within ±2°.
//   - palette: the strip's CSS chip styles carry gradients as strings; the
//     SVG needs real colours, so chipFacePalette parses the two stops and
//     derives the darker rim family from them.
//   - stripes: every chip's edge stripes sit at index-derived angular
//     phases — stacked chips read as individually rotated real chips, not
//     one repeated sprite. Widths foreshorten towards the silhouette.
//   - the top disc prints NOTHING (v13): a mixed stack reads by colour and
//     the €-amount right below the stack states the total. denominationInk
//     stays exported for the strip's printed chips.
// ═══════════════════════════════════════════════════════════════════════

// One CSS chip template of the strip's denomination ladder (`bg` is a
// two-stop linear-gradient STRING — chipFacePalette parses it for the SVG).
export interface ChipStyle {
  bg: string
  border: string
  color: string
}

// Visual chip families, ONE per ladder rung. Rungs 0–5 mirror the
// ChipPlacementPanel cycle (grey 1, teal 5, blue 10, red 25, amber 50,
// green 100) so crash chips read like the other games' — but the crash
// ladder is 8 rungs and the banded stack reads by COLOUR ALONE, so the top
// end diverges (v13 fix round, pixel findings):
//   - 250 is a PINK family (classic casino high-denom pink) — its old
//     black-face/purple-ring template was indistinguishable from the €1
//     grey edge-on, where the ring is invisible.
//   - 500 gets its OWN eighth family, the classic casino lavender/purple —
//     the previous 7-entry cycle wrapped index 7 back onto the €1 grey, so
//     every stake ≥ €500 misread as pennies in the pile.
// The €1 grey stays the ladder's ONLY dark-neutral: "lots of black at the
// base" now truthfully means a pile of €1s (chipStack.test.ts pins the
// rim-view separability of all eight families).
export const CHIP_STYLES: ChipStyle[] = [
  { bg: 'linear-gradient(160deg, #3d3d3d, #1a1a1a)', border: '#666666', color: '#bbb' },
  { bg: 'linear-gradient(160deg, #1a5c4c, #0a3325)', border: '#28b890', color: '#5eeac4' },
  { bg: 'linear-gradient(160deg, #2a4a8a, #142850)', border: '#4488dd', color: '#99ccff' },
  { bg: 'linear-gradient(160deg, #b83030, #6b1a1a)', border: '#e05050', color: '#ffbbbb' },
  { bg: 'linear-gradient(160deg, #c47b10, #7a4d08)', border: '#e8a020', color: '#ffe0a0' },
  { bg: 'linear-gradient(160deg, #1f8a1f, #0d4a0d)', border: '#30b030', color: '#90ee90' },
  // The two big families keep their BOTTOM stops a step brighter than the
  // reference ramp: a buried chip shows mostly the lower half of its rim
  // gradient (the chip above overlaps the rim top), so the bottom stop is
  // what a banded pile actually samples — the very mechanism that made the
  // old dark 250/500 collapse into shadow (v13 fix-round iteration 2).
  { bg: 'linear-gradient(160deg, #e0559d, #9e3169)', border: '#ff8fc8', color: '#ffd0e8' },
  { bg: 'linear-gradient(160deg, #8a5fd6, #553596)', border: '#b492f0', color: '#dcc8ff' }
]

/**
 * Style for a chip at a given index in the strip's ladder. The modulo is a
 * SAFETY for hypothetical ladders longer than the palette — the standard
 * 8-rung ladder maps one-to-one (the v13 7-cycle wrap bug is pinned by
 * tests).
 */
export function chipStyleForIndex(idx: number): ChipStyle {
  return CHIP_STYLES[idx % CHIP_STYLES.length]!
}

/**
 * Map a staked amount to the family of the highest denomination chip it
 * meets or exceeds — a placed-stack accent (card rim glow) reads as the
 * chip that built it (mirrors ChipPlacementPanel's `getChipStyleForAmount`).
 * `chips` are the strip's denomination values (ascending).
 */
export function chipStyleForAmount(amount: number, chips: number[]): ChipStyle {
  for (let i = chips.length - 1; i >= 0; i--) {
    const value = chips[i]
    if (value !== undefined && amount >= value) return chipStyleForIndex(i)
  }
  return chipStyleForIndex(0)
}

// Sane visual cap — the card's stack well fits 7 rims at CHIP_LIFT_PX
// without invading the eyebrow row above it.
export const CHIP_STACK_MAX = 7

// One rim height per chip — how far each chip sits above the one below
// (px). Tuned a hair UNDER the on-screen rim depth (CHIP_RIM_H 3.4 in a
// 38-wide viewBox drawn at 42px ⇒ ~3.75px) so stacked chips sit snug like
// a real pile, and the full 7-chip stack overdraws its 30px well by ~10px
// — clear of the eyebrow text (verified in the harness captures).
export const CHIP_LIFT_PX = 3.5

// COMPRESSION rule 1: no band draws more than four discs — beyond four the
// eye stops counting and the band just reads "a lot of that colour".
const CHIP_BAND_MAX = 4

// Floating-point slack for the greedy division: stakes are sums of chip
// taps, so `remaining / denom` is an integer up to fp noise (~1e-15
// relative). The slack absorbs that noise without ever promoting a real
// fraction (the smallest real fraction of interest is a currency cent).
const GREEDY_SLACK = 1e-6

// One band of the croupier stack: `count` discs of one `denom` family.
export interface ChipStackBand {
  denom: number
  count: number
}

/**
 * Greedy croupier decomposition of `amount` over the strip's `chips`
 * ladder (ascending denominations), returned largest-denomination FIRST —
 * i.e. in draw order, bottom of the stack to top.
 *
 * Rules (deterministic — same amount, same stack):
 *   1. GREEDY: largest denomination first, `floor(remaining / denom)` per
 *      band (with fp slack so exact multiples never smear into dust).
 *   2. ROUND UP: any positive remainder below the smallest chip — including
 *      a whole stake below the smallest denomination — folds into ONE more
 *      smallest chip. A non-zero stake never renders zero chips.
 *   3. COMPRESSION (only when the raw pile exceeds CHIP_STACK_MAX discs):
 *      a. SATURATE: every band's count clamps to CHIP_BAND_MAX (4).
 *      b. EAT THE SMALL END: while still over the cap, remove one disc from
 *         the smallest surviving band; a band emptied disappears. The big
 *         money keeps the base, and the finest SURVIVING band stays on top
 *         (what the eye reads last). The €-amount text below the stack
 *         remains the source of truth for the total.
 */
export function chipStackComposition(amount: number, chips: number[]): ChipStackBand[] {
  if (amount <= 0 || chips.length === 0) return []
  const smallest = chips[0]
  if (smallest === undefined || smallest <= 0) return []

  // 1. Greedy, largest denomination first.
  const bands: ChipStackBand[] = []
  let remaining = amount
  for (let i = chips.length - 1; i >= 0; i--) {
    const denom = chips[i]
    if (denom === undefined || denom <= 0) continue
    const count = Math.floor(remaining / denom + GREEDY_SLACK)
    if (count <= 0) continue
    bands.push({ denom, count })
    remaining -= count * denom
  }

  // 2. Round the sub-minimum remainder UP into the smallest-chip band.
  if (remaining > smallest * GREEDY_SLACK) {
    const last = bands[bands.length - 1]
    if (last && last.denom === smallest) last.count += 1
    else bands.push({ denom: smallest, count: 1 })
  }

  // 3. Compression — only when the raw pile exceeds the visible cap.
  let total = bands.reduce((sum, band) => sum + band.count, 0)
  if (total > CHIP_STACK_MAX) {
    // 3a. Saturate every band at CHIP_BAND_MAX.
    for (const band of bands) {
      if (band.count > CHIP_BAND_MAX) {
        total -= band.count - CHIP_BAND_MAX
        band.count = CHIP_BAND_MAX
      }
    }
    // 3b. Eat the small end, one disc at a time.
    while (total > CHIP_STACK_MAX && bands.length > 0) {
      const smallestBand = bands[bands.length - 1]!
      smallestBand.count -= 1
      total -= 1
      if (smallestBand.count === 0) bands.pop()
    }
  }

  return bands
}

// One disc of the flattened stack, bottom→top. `denomIndex` is the ladder
// slot (→ chipStyleForIndex family); `key` is band-relative (`denom:i`) so
// growing a band keeps every existing disc's React node.
export interface ChipStackDisc {
  denom: number
  denomIndex: number
  key: string
}

/**
 * The composition flattened into draw order: base disc first (largest
 * denomination), finest band last (top of the pile).
 */
export function chipStackDiscs(amount: number, chips: number[]): ChipStackDisc[] {
  const bands = chipStackComposition(amount, chips)
  const discs: ChipStackDisc[] = []
  for (const band of bands) {
    const denomIndex = chips.indexOf(band.denom)
    for (let i = 0; i < band.count; i++) {
      discs.push({ denom: band.denom, denomIndex, key: `${band.denom}:${i}` })
    }
  }
  return discs
}

/**
 * Which disc — if any — plays the one-shot settle animation this render.
 *
 *   - Fresh mount (prevKeys null): only a SINGLE-disc pile settles (the
 *     first chip placed on an empty card); a card remounting with a full
 *     pile must not fire an animation storm.
 *   - Exactly one key added and none removed → the added disc settles (the
 *     chip just placed).
 *   - Anything else — greedy re-carve, compression restructure, doubling,
 *     undo/clear — settles NOTHING: the stack re-renders in place.
 */
export function chipStackSettleKey(
  prevKeys: readonly string[] | null,
  nextKeys: readonly string[]
): string | null {
  if (prevKeys === null) return nextKeys.length === 1 ? nextKeys[0]! : null
  const prev = new Set(prevKeys)
  const next = new Set(nextKeys)
  for (const key of prev) {
    if (!next.has(key)) return null // any removal = restructure
  }
  const added = nextKeys.filter((key) => !prev.has(key))
  return added.length === 1 ? added[0]! : null
}

// One chip's resting pose in the pile. dx/rot are the natural-scatter
// offsets (px / deg) around the stack's centre line; lift is how far up the
// pile the chip sits (px — one rim height per level).
export interface ChipStackSeat {
  dx: number
  rot: number
  lift: number
}

/**
 * Deterministic pile layout for `depth` chips. The base chip anchors the
 * pile dead-centre; the chips above alternate sides with index-derived
 * magnitudes (dx ∈ ±[1..3]px, rot ∈ ±[1..2]°) so the pile reads naturally
 * placed — never sheared to one side, never random, never toppling (the
 * drawn rim depth carries the realism, not big throws).
 * Seat i is identical whatever the depth: adding a chip only ADDS a seat.
 */
export function chipStackLayout(depth: number): ChipStackSeat[] {
  const seats: ChipStackSeat[] = []
  for (let i = 0; i < depth; i++) {
    if (i === 0) {
      seats.push({ dx: 0, rot: 0, lift: 0 })
      continue
    }
    const side = i % 2 === 1 ? 1 : -1
    seats.push({
      dx: side * (1 + ((i * 7) % 9) / 4),
      rot: side * (1 + ((i * 3) % 4) * 0.34),
      lift: i * CHIP_LIFT_PX
    })
  }
  return seats
}

/** #rrggbb (with or without #) darkened by `factor`; bad input → dark grey. */
export function darkenHex(hex: string, factor: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex)
  if (!m) return '#1a1a1a'
  const n = parseInt(m[1]!, 16)
  const channel = (shift: number) =>
    Math.min(255, Math.max(0, Math.round(((n >> shift) & 255) * factor)))
  const out = (channel(16) << 16) | (channel(8) << 8) | channel(0)
  return `#${out.toString(16).padStart(6, '0')}`
}

// WCAG relative luminance of a #rgb / #rrggbb hex; unparseable → -1.
function hexLuminance(hex: string): number {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex)
  if (!m) return -1
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!
  const lin = (i: number) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4)
}

// The printed value on the top chip uses the family's pastel accent ink —
// but the two darkest families' accents (#bbb grey, #c8a8f0 purple) sit
// below the brightness band the sibling chips set, so their numerals read
// dim at chip size (v12.4 pixel review). Inks under this luminance floor
// print in the warm stripe white instead.
const DENOMINATION_INK_LUMINANCE_FLOOR = 0.55
const DENOMINATION_INK_FALLBACK = '#f2efe6'

/** The ink the top chip's printed denomination uses (legibility-floored). */
export function denominationInk(cssInk: string): string {
  const lum = hexLuminance(cssInk)
  return lum >= DENOMINATION_INK_LUMINANCE_FLOOR ? cssInk : DENOMINATION_INK_FALLBACK
}

// The colour family one SVG chip is drawn in — parsed from the strip's CSS
// chip template (whose `bg` is a two-stop linear-gradient STRING).
export interface ChipFacePalette {
  faceTop: string // face gradient, lit edge
  faceBottom: string // face gradient, shaded edge
  side: string // rim front, lit
  sideDark: string // rim front, shadow floor
  ring: string // printed face ring / border accent
}

/**
 * Parse a CHIP_STYLES entry into the solid colours the SVG chip needs.
 * The face keeps the CSS gradient's two stops; the rim family is the same
 * hue pushed into shadow (a real chip's side is its own colour, unlit).
 * Not a two-stop gradient → the neutral dark-chip fallback.
 */
export function chipFacePalette(bg: string, border: string): ChipFacePalette {
  const stops = bg.match(/#[0-9a-f]{6}/gi)
  const faceTop = stops && stops.length >= 2 ? stops[0]! : '#3d3d3d'
  const faceBottom = stops && stops.length >= 2 ? stops[1]! : '#1a1a1a'
  return {
    faceTop,
    faceBottom,
    side: darkenHex(faceTop, 0.72),
    sideDark: darkenHex(faceBottom, 0.55),
    ring: border
  }
}

// One visible edge stripe on the front rim: x = centre as a fraction of rx
// along the front arc (−1 left silhouette … 1 right), w = width as a
// fraction of the full stripe width (foreshortens towards the edges).
export interface ChipEdgeStripe {
  x: number
  w: number
}

// Six edge spots around the chip (real casino chips carry 4–8); the front
// half shows 2–3 of them depending on the chip's rotational phase.
const STRIPE_COUNT = 6
// Per-chip phase step (deg) — coprime-ish with the 60° spot spacing so
// consecutive chips in a pile never repeat a stripe pattern.
const STRIPE_PHASE_STEP_DEG = 23
// A spot is drawn only while it faces the viewer enough to read as a
// stripe (sin > 0.25) and hasn't collapsed into the silhouette edge.
const STRIPE_MIN_FACING = 0.25
const STRIPE_MAX_X = 0.95

/**
 * The visible front-rim stripes for the chip at `index` in a pile.
 * Deterministic: the index sets the rotational phase, nothing else.
 */
export function chipEdgeStripes(index: number): ChipEdgeStripe[] {
  const phase = ((index * STRIPE_PHASE_STEP_DEG) % 360) * (Math.PI / 180)
  const stripes: ChipEdgeStripe[] = []
  for (let k = 0; k < STRIPE_COUNT; k++) {
    const angle = phase + (k * 2 * Math.PI) / STRIPE_COUNT
    const facing = Math.sin(angle)
    if (facing <= STRIPE_MIN_FACING) continue
    const x = Math.cos(angle)
    if (Math.abs(x) >= STRIPE_MAX_X) continue
    stripes.push({ x, w: facing })
  }
  return stripes.sort((a, b) => a.x - b.x)
}
